import { useCallback, useRef, useState, useEffect } from 'react';

export interface UseResizableWidthOptions {
  /** Starting width in pixels. */
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Which edge the handle lives on — determines whether dragging shrinks or
   *  grows the panel. 'left' = panel is anchored right, drag handle on the
   *  left edge (this is the default for right-side drawers like
   *  AgentAssistant). 'right' = mirror. */
  resizeFrom?: 'left' | 'right';
  /** Persist the current width in localStorage under this key. If omitted,
   *  width resets to `defaultWidth` on every mount. */
  storageKey?: string;
}

export interface UseResizableWidthResult {
  width: number;
  /** Spread onto the drag handle's onMouseDown. Starts a drag + listens for
   *  move / up at document level so the drag survives mouse leaving the
   *  handle. */
  onResizeStart: (e: React.MouseEvent) => void;
}

/**
 * Extracted drag-to-resize logic from the original AssistantPanel (now
 * AgentAssistant). Reused by AgentAssistant itself and by the entity wizard
 * sidebar (§4.4 of the entity-wizard spec).
 */
export function useResizableWidth({
  defaultWidth,
  minWidth,
  maxWidth,
  resizeFrom = 'left',
  storageKey,
}: UseResizableWidthOptions): UseResizableWidthResult {
  const [width, setWidth] = useState<number>(() => {
    if (!storageKey) return defaultWidth;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return defaultWidth;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return defaultWidth;
      return Math.max(minWidth, Math.min(maxWidth, parsed));
    } catch {
      return defaultWidth;
    }
  });
  const resizing = useRef(false);

  useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, String(width));
    } catch {
      /* quota exceeded or disabled — ignore */
    }
  }, [storageKey, width]);

  const onResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      resizing.current = true;
      const startX = e.clientX;
      const startW = width;
      const onMove = (ev: MouseEvent) => {
        if (!resizing.current) return;
        // When handle is on the LEFT edge of a right-anchored panel, dragging
        // left (smaller clientX) should GROW the panel. So delta = startX - ev.clientX.
        const delta = resizeFrom === 'left' ? startX - ev.clientX : ev.clientX - startX;
        setWidth(Math.max(minWidth, Math.min(maxWidth, startW + delta)));
      };
      const onUp = () => {
        resizing.current = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [width, minWidth, maxWidth, resizeFrom],
  );

  return { width, onResizeStart };
}
